import * as THREE from 'three';
import { Entity } from './Entity.js';
import { Part } from '../parts/Part.js';
import { createPartMesh } from '../parts/PartRegistry.js';
import type { Circle } from '../systems/Collision.js';

/**
 * Pickup entity - a dropped part that can be collected.
 *
 * Pickups float/bob in the air and have a glowing effect to attract attention.
 * When the player presses E nearby, they swap the pickup part with their equipped part.
 *
 * Visual:
 * - Part's mesh with a pulsing glow/emissive effect
 * - Gentle floating/bobbing animation
 * - Slightly larger collision radius for easier pickup
 *
 * Collision:
 * - Circle shape for detection
 * - Larger than visual for forgiving interaction
 */
export class Pickup extends Entity {
  /** The part contained in this pickup */
  private part: Part;

  /** Bob animation offset (radians) */
  private bobOffset: number = 0;

  /** Base height for the floating animation */
  private readonly baseHeight: number = 0.5;

  /** Bob animation speed (radians per second) */
  private readonly bobSpeed: number = 2;

  /** Bob animation amplitude (world units) */
  private readonly bobAmplitude: number = 0.2;

  /** Glow animation offset (radians) */
  private glowOffset: number = 0;

  /** Glow animation speed (radians per second) */
  private readonly glowSpeed: number = 3;

  /** Collision radius for pickup detection */
  private readonly collisionRadius: number = 0.8;

  /** The glow material for pulsing effect */
  private glowMaterial: THREE.MeshStandardMaterial | null = null;

  /** The glow mesh (sphere) for pulsing effect */
  private glowMesh: THREE.Mesh | null = null;

  /** The point light for enhanced glow */
  private light: THREE.PointLight | null = null;

  constructor(x: number, y: number, part: Part) {
    super(x, y, 0);
    this.part = part;

    // Random starting offset for visual variety
    this.bobOffset = Math.random() * Math.PI * 2;
    this.glowOffset = Math.random() * Math.PI * 2;

    this.buildMesh();
    this.updateMeshTransform();
  }

  /**
   * Build the visual representation of the pickup.
   *
   * Creates the part mesh with a glowing background effect.
   */
  private buildMesh(): void {
    this.mesh = new THREE.Group();

    // Create the part mesh
    const partMesh = createPartMesh(this.part);

    // Add a glowing background sphere
    const glowGeometry = new THREE.SphereGeometry(0.6, 16, 12);
    this.glowMaterial = new THREE.MeshStandardMaterial({
      color: 0xffeb3b, // Yellow pickup color
      emissive: 0xffeb3b, // Glowing yellow
      emissiveIntensity: 0.8,
      transparent: true,
      opacity: 0.3,
      metalness: 0,
      roughness: 1,
    });

    this.glowMesh = new THREE.Mesh(glowGeometry, this.glowMaterial);
    this.mesh.add(this.glowMesh);

    // Add the part mesh on top, centered
    partMesh.position.set(0, 0, 0);
    this.mesh.add(partMesh);

    // Store reference for part
    this.part.mesh = partMesh;

    // Add a subtle point light for extra glow effect
    this.light = new THREE.PointLight(0xffeb3b, 0.5, 3);
    this.light.position.set(0, 0, 0);
    this.mesh.add(this.light);
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // Update bob animation
    this.bobOffset += this.bobSpeed * dtSec;
    const bobHeight = Math.sin(this.bobOffset) * this.bobAmplitude;

    // Enhanced glow animation - pulse at 2x the bob frequency for more energy
    const glowIntensity = 0.5 + Math.sin(this.bobOffset * 2) * 0.3; // Pulse between 0.2 and 0.8

    // Apply animations
    if (this.mesh) {
      this.mesh.position.y = this.baseHeight + bobHeight;

      // Update glow material emissive intensity
      if (this.glowMaterial) {
        this.glowMaterial.emissiveIntensity = glowIntensity;
      }

      // Pulse the point light intensity as well
      if (this.light) {
        this.light.intensity = glowIntensity;
      }

      // Pulse the glow mesh emissive (if it has emissive)
      if (this.glowMesh) {
        const mat = this.glowMesh.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity = glowIntensity;
      }
    }

    // Update horizontal transform (x, z position)
    this.updateMeshTransform();
  }

  /**
   * Override updateMeshTransform to preserve Y position from bob animation.
   */
  protected updateMeshTransform(): void {
    if (this.mesh) {
      // Only update X and Z, preserve Y from bob animation
      this.mesh.position.x = this.position.x;
      this.mesh.position.z = this.position.y;
      // Y is handled by bob animation in update()

      // Pickups don't rotate
      this.mesh.rotation.y = 0;
    }
  }

  /**
   * Get the part contained in this pickup.
   */
  getPart(): Part {
    return this.part;
  }

  /**
   * Get the collision shape for pickup detection.
   *
   * Uses a larger radius than visual for forgiving interaction.
   */
  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }

  /**
   * Clean up resources when pickup is collected/destroyed.
   */
  destroy(): void {
    // Dispose of glow material
    if (this.glowMaterial) {
      this.glowMaterial.dispose();
    }

    super.destroy();
  }
}
